import { useState, useMemo } from 'react';
import cn from 'classnames';
import Input from '@/components/input';
import InputPhone from '@/components/InputPhone';
import BotonFormUnico from '../Botones/Formulario/Unico';
import axios from 'axios';
import { isEmail, isString, isUrl } from '@/utils/common';
import { normalizeEmail } from '@/utils/normalizeEmail';
import RecaptchaLegend from '@/components/RecaptchaLegend';
import RadioForm from '@/components/Radio';
import { useValidateRecaptcha } from '@/hooks/useValidateRecaptcha';
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect';
import SelectPlataformaEcommerce from '@/components/SelectPlataformaEcommerce';

const Form = ({ finish, dict, partner, campaign = '' }) => {
    const { validateRecaptcha } = useValidateRecaptcha();
    const [form, setForm] = useState({ url_website: '' });
    const [check, setCheck] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isCheckingUrl, setIsCheckingUrl] = useState(false);
    const [isUrlValid, setIsUrlValid] = useState(true);

    const isAR = dict.country_short_name === 'AR';

    useDebouncedEffect(() => {
        const validateUrl = async () => {
            if (!form.url_website) {
                setIsUrlValid(true);
                setIsCheckingUrl(false);
                return;
            }

            setIsCheckingUrl(true);
            const valid = await isUrl(form.url_website);
            setIsUrlValid(valid);
            setIsCheckingUrl(false);
        };

        validateUrl();
    }, [form.url_website], 300);

    const handleUrlChange = (e) => {
        setIsCheckingUrl(true);
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const onChangeInvoiceType = (e) => {
        setForm((prevForm) => ({
            ...prevForm,
            invoice_type: e.target.value,
        }));
    };

    const handlePhoneChange = (e) => {
        setForm((prevForm) => ({
            ...prevForm,
            phone: e.target.value,
            isValidPhone: e.target.isValid,
        }));
        setCheck(e.target.isValid);
    };

    const isFormComplete = useMemo(() => {
        setCheck(false);

        return (
            isString(form.seller_name) &&
            form.platform_ecommerce_id?.name &&
            isString(form.tax_identification, 7, 13) &&
            isString(form.name) &&
            isString(form.average_shipments_per_month, 1) &&
            form.isValidPhone &&
            isEmail(form.email) &&
            isUrlValid &&
            !isCheckingUrl &&
            (!isAR || form.invoice_type)
        );
    }, [form, isUrlValid, isCheckingUrl, isAR]);

    const onChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const onChangeSelect = (e) => {
        setForm({
            ...form,
            [e.target.name]: {
                id: e.target.value,
                name: e.target.platform_ecommerce,
            },
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!isFormComplete) {
            setCheck(true);
            return;
        }
        setLoading(true);

        try {
            const result = await validateRecaptcha();

            if (!result.success) {
                console.error('Error validando reCAPTCHA:');
                setLoading(false);
                return;
            }

            await axios.post(location.origin + '/api/partner', {
                ...form,
                email: normalizeEmail(form.email),
                platform_ecommerce: form.platform_ecommerce_id.name,
                partner: partner,
                campaign: campaign,
                lead_email: dict.lead_email,
            });
            finish();
        } catch (error) {
            console.error('Error al enviar el formulario:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <input type='hidden' name='partner' value={partner} />
            <div className='mb-24'>
                <Input
                    type='text'
                    name='seller_name'
                    id='seller_name'
                    placeholder='Nombre del comercio'
                    onChange={onChange}
                    hasError={check && !isString(form.seller_name)}
                />
            </div>
            <div className='mb-24'>
                <div className='text-grey-dark'>{'Plataforma E-commerce'}</div>
                <SelectPlataformaEcommerce
                    check={check}
                    onChange={onChangeSelect}
                />
                {check && !form.platform_ecommerce_id?.name && (
                    <span className='text-red'>
                        Por favor selecciona una plataforma e-commerce
                    </span>
                )}
            </div>
            <div className='mb-24'>
                <Input
                    type='text'
                    name='url_website'
                    id='url_website'
                    placeholder='URL de tu marca'
                    onChange={handleUrlChange}
                    hasError={check && !isUrlValid}
                />
                {check && !isUrlValid && (
                    <span className='text-red'>
                        Por favor ingrese una URL valida o deje el campo sin
                        completar.
                    </span>
                )}
            </div>
            <div className='mb-24'>
                <Input
                    type='text'
                    name='tax_identification'
                    id='tax_identification'
                    placeholder={dict.id_type}
                    onChange={onChange}
                    hasError={
                        check && !isString(form.tax_identification, 7, 13)
                    }
                />
            </div>
            {isAR && (
                <div className='mb-24'>
                    <div className='text-grey-dark mb-10'>
                        {'Tipo de factura'}
                    </div>
                    <div className='flex items-center duration-30'>
                        <RadioForm
                            label='Factura A'
                            name='invoice_type'
                            value='A'
                            onChange={onChangeInvoiceType}
                            checked={form.invoice_type === 'A'}
                        />
                        <RadioForm
                            label='Factura B'
                            name='invoice_type'
                            value='B'
                            onChange={onChangeInvoiceType}
                            checked={form.invoice_type === 'B'}
                        />
                        <span
                            className={cn(
                                'opacity-0 duration-30 text-red text-20 translate-x-10',
                                {
                                    '!opacity-100 !translate-x-0':
                                        check && !form.invoice_type,
                                }
                            )}
                        >
                            *
                        </span>
                    </div>
                </div>
            )}
            <div className='mb-24'>
                <Input
                    type='text'
                    name='name'
                    id='name'
                    placeholder='Nombre de contacto'
                    onChange={onChange}
                    hasError={check && !isString(form.name)}
                />
            </div>
            <div className='mb-24'>
                <Input
                    type='email'
                    name='email'
                    id='email'
                    placeholder='Email'
                    onChange={onChange}
                    hasError={check && !isEmail(form.email)}
                />
            </div>
            <div className='mb-24'>
                <InputPhone
                    country_short_name={dict.country_short_name}
                    name='phone'
                    onChange={handlePhoneChange}
                />
            </div>
            <div className='mb-24'>
                <Input
                    placeholder='Envíos promedio al mes'
                    name='average_shipments_per_month'
                    type='number'
                    hasError={
                        check && !isString(form.average_shipments_per_month, 0)
                    }
                    onChange={onChange}
                />
                {check && !isString(form.average_shipments_per_month, 0) && (
                    <span className='text-red'>
                        Por favor ingresa un promedio.
                    </span>
                )}
            </div>
            <div className='flex justify-center'>
                <div className='max-w-[21rem]'>
                    <BotonFormUnico
                        key={'submit-partner-form'}
                        bgColor='button-gradient'
                        type='submit'
                        text={loading ? 'Enviando...' : 'Enviar'}
                        disabled={!isFormComplete || loading}
                    />
                </div>
            </div>
            <RecaptchaLegend />
        </form>
    );
};

export default Form;
